// Tailwind class strings for the shared table look of DataTable: the
// app-style grammar of the MUI table overrides
// (styles/msiMuiComponentOverrides.js MuiTableCell/MuiTableRow), with every
// colour from the theme variables applyThemeVars writes (see
// tailwind.config.js `tbl` and `ui`). The Watchlist grid's look
// (watchlist_table/grid/watchlistGridStyles.js) draws on the same tokens.
//
// Sizes come from CSS variables set on the frame, so compact mode
// (`data-compact` on <html>) is one rule here: --tbl-row-h and --tbl-head-h
// (the frame's inline --tbl-row-base / --tbl-head-base, 8px less when
// compact, as MUI's compact cells lose 4px of vertical padding each side)
// and --tbl-cell-px (8px, 4px when compact).
//
// Each property has one class per element, so the order Tailwind emits
// utilities in never matters. A caller's class that must beat one of these
// (a row tint over the stripe, a cell background over a pinned cell's
// inherited one, a padding) uses Tailwind's `!` prefix, as the app's
// DataGrid overrides already do.

// The scroll container is also the visual frame: 1px border, 8px radius,
// surface background, content clipped to the radius.
export const frameClassName = 'relative overflow-auto rounded-lg border border-solid border-hairline bg-ui-surface '
    + '[--tbl-row-h:var(--tbl-row-base)] [--tbl-head-h:var(--tbl-head-base)] [--tbl-cell-px:8px] '
    + '[[data-compact]_&]:[--tbl-row-h:calc(var(--tbl-row-base)_-_8px)] '
    + '[[data-compact]_&]:[--tbl-head-h:calc(var(--tbl-head-base)_-_8px)] '
    + '[[data-compact]_&]:[--tbl-cell-px:4px]';

// Body type for every cell; header cells set their own. Separate borders so
// sticky cells keep theirs.
export const tableClassName = 'm-0 border-separate border-spacing-0 text-[12.5px] font-medium leading-[1.35] '
    + 'tracking-normal text-content-primary';
export const tableLayoutClassNames = Object.freeze({
    fixed: 'w-full table-fixed',
    // Content-sized columns; at least as wide as the frame.
    auto: 'w-max min-w-full table-auto',
});

export const alignClassNames = Object.freeze({
    left: 'text-left',
    center: 'text-center',
    right: 'text-right',
});

// Flex alignment of a header's label, sort icon and controls. Right-aligned
// columns put the sort icon before the label, as DataGrid does.
export const headerAlignClassNames = Object.freeze({
    left: 'justify-start',
    center: 'justify-center',
    right: 'justify-end',
});

export const headerRowClassName = 'h-[var(--tbl-head-h)]';

// Header cells stick to the top of the frame (their `top` is set inline per
// header row) on the header background, above body cells.
const headerCellBase = 'sticky border-0 border-b border-solid border-hairline bg-tbl-header px-[var(--tbl-cell-px)] '
    + 'py-0 align-middle text-[11px] font-extrabold uppercase leading-[1.2] tracking-normal whitespace-nowrap';
export const headerCellClassNames = Object.freeze({
    plain: `${headerCellBase} z-[3]`,
    // Pinned header cells stick on both axes, above other header cells.
    pinned: `${headerCellBase} z-[4]`,
});

// A header's text label: cut off on one line, or (DataTable `wrapHeaders`)
// wrapped onto two lines at a tighter line height, which still fit the
// header height in compact mode, then cut off.
export const headerLabelClassNames = Object.freeze({
    truncate: 'truncate',
    wrap: 'line-clamp-2 whitespace-normal break-words leading-[1.15]',
});

// The sort button fills the header cell's text area.
export const sortButtonClassName = 'group flex min-w-0 flex-1 cursor-pointer items-center gap-1 rounded-sm border-0 '
    + 'bg-transparent p-0 text-inherit focus:outline-none focus-visible:outline focus-visible:outline-2 '
    + 'focus-visible:outline-ui-focus';
export const sortIconClassNames = Object.freeze({
    sorted: 'shrink-0',
    // Unsorted columns take no room for the icon (so labels keep the width,
    // as in DataGrid) and preview their first direction on hover and focus.
    unsorted: 'hidden shrink-0 opacity-50 group-hover:block group-focus-visible:block',
});

export const rowClassName = 'h-[var(--tbl-row-h)]';
export const rowStateClassNames = Object.freeze({
    even: 'bg-ui-surface hover:bg-ui-row-hover',
    odd: 'bg-tbl-stripe hover:bg-ui-row-hover',
    // Not striped and no hover: full-width and summary rows. Loading rows
    // alternate plain and plainOdd (the stripe without the hover).
    plain: 'bg-ui-surface',
    plainOdd: 'bg-tbl-stripe',
    // Selected rows keep their tint on hover; the accent sits on the first
    // cell because an opaque sticky cell would hide one drawn on the row.
    selected: 'bg-tbl-selected [&>td:first-child]:shadow-[inset_3px_0_theme(colors.tbl.accent)]',
    // Rows whose checkbox is ticked: the selected tint, without the accent
    // (as DataGrid's checkbox selection showed them).
    checked: 'bg-tbl-selected',
});
// Rows with onRowClick: pointer, and a focus ring for keyboard focus only.
export const clickableRowClassName = 'cursor-pointer focus:outline-none focus-visible:outline focus-visible:outline-2 '
    + 'focus-visible:-outline-offset-2 focus-visible:outline-ui-focus';
export const summaryRowClassName = 'font-bold';

// Body cells: hairline row divider (half a pixel and fainter in the
// high-contrast themes), vertically centred, truncated with an ellipsis.
const bodyCellBase = 'border-0 border-b border-solid border-tbl-divider py-0 align-middle '
    + '[html[data-theme^=highContrast]_&]:border-b-[0.5px] '
    + '[html[data-theme^=highContrast]_&]:border-b-[rgb(var(--c-border)/0.24)]';
export const cellPaddingClassNames = Object.freeze({
    normal: 'px-[var(--tbl-cell-px)]',
    none: 'px-0',
});
export const bodyCellClassName = bodyCellBase;
// The optimized (OPT) column tint: a translucent `--c-optimized` layer drawn
// over whatever the row shows beneath it (stripe, hover, selected, a state
// tint, a pinned cell's inherited background). Give it to a column as a
// string `meta.cellClassName`, so its loading skeleton cells are tinted too.
// A background image, so it composes with the background colour beneath.
export const optimizedCellClassName = 'bg-gradient-to-b from-tbl-optimized/[0.14] to-tbl-optimized/[0.14]';
// A row's single cell spanning every column (DataTable renderFullWidthRow).
export const fullWidthCellClassName = `${bodyCellBase} px-[var(--tbl-cell-px)]`;
export const truncateClassName = 'overflow-hidden text-ellipsis whitespace-nowrap';
// Pinned cells are opaque with the row's own background (stripe, hover,
// selected or a caller's tint), so the row state shows through the pinned
// region. Summary-row cells also stick vertically, above body cells.
export const stickyCellClassNames = Object.freeze({
    pinned: 'sticky z-[1] bg-inherit',
    summary: 'sticky z-[2] bg-inherit',
    summaryPinned: 'sticky z-[3] bg-inherit',
});
// The last pinned column's right edge.
export const lastPinnedClassName = 'border-r border-r-hairline';

// No divider under the body's last row; the summary groups are set off by a
// hairline.
export const bodyGroupClassName = '[&>tr:last-child>td]:border-b-0';
export const topSummaryGroupClassName = '[&>tr:last-child>td]:border-b-hairline';
export const bottomSummaryGroupClassName = '[&>tr:first-child>td]:border-t [&>tr:first-child>td]:border-t-hairline '
    + '[&>tr:last-child>td]:border-b-0';

// Empty cells that absorb the width left over by fixed px columns.
export const fillerCellClassName = 'p-0 border-0 border-b border-solid border-tbl-divider';
export const fillerHeaderCellClassName = 'sticky z-[3] border-0 border-b border-solid border-hairline bg-tbl-header p-0';

export const emptyCellClassName = 'h-[calc(var(--tbl-row-h)*2)] px-4 py-3 text-center text-content-secondary';
// The spacer rows above and below a virtualized window.
export const spacerCellClassName = 'border-0 p-0';
